import { useState, useEffect, useRef } from 'react';
import { useStudioStore } from '../../store/useStudioStore';
import { Mic, Square, Pause, Play, Trash2, AlertCircle, Upload } from 'lucide-react';
import { uploadRecording, getRecordingUrl } from '../../utils/audioUpload';

export const AudioRecorder = () => {
  const {
    isRecording,
    recordingTakes,
    selectedTrackId,
    channels,
    addRecordingTake,
    removeRecordingTake,
    setIsRecording,
    setRecordingStartTime,
    setSelectedTrackId,
  } = useStudioStore();

  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Request microphone permission and set up audio input
  const requestMicrophoneAccess = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });
      setAudioStream(stream);

      // Set up audio context for level metering
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start level monitoring
      monitorInputLevel();

      return stream;
    } catch (err) {
      console.error('Microphone access denied:', err);
      setError('Microphone access denied. Please check your browser permissions.');
      return null;
    }
  };

  // Monitor input level for visual feedback
  const monitorInputLevel = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const normalizedLevel = average / 255;
      setInputLevel(normalizedLevel);

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  };

  // Start recording
  const startRecording = async () => {
    let stream = audioStream;
    
    if (!stream) {
      stream = await requestMicrophoneAccess();
      if (!stream) return;
    }

    try {
      // Create MediaRecorder instance
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      audioChunksRef.current = [];

      // Handle data available event
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop event
      recorder.onstop = async () => {
        await saveRecording();
      };

      // Start recording
      recorder.start(100); // Collect data every 100ms
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingStartTime(Date.now());
      setRecordingTime(0);
      setIsPaused(false);

      // Start timer
      timerIntervalRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Failed to start recording. Please try again.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setIsRecording(false);
    setIsPaused(false);
  };

  // Pause/resume recording
  const togglePause = () => {
    if (!mediaRecorder) return;

    if (mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setIsPaused(true);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    } else if (mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setIsPaused(false);
      timerIntervalRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  // Save recording
  const saveRecording = async () => {
    if (audioChunksRef.current.length === 0) {
      console.warn('No audio chunks to save');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Create blob from audio chunks
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

      // Generate waveform data (simplified)
      const waveform = await generateWaveform(blob);

      // Upload to R2 storage via backend API
      const uploadResult = await uploadRecording({
        file: blob,
        title: `Recording ${new Date().toLocaleString()}`,
        durationSeconds: recordingTime,
        waveformData: waveform,
      });

      // Get the full CDN URL for playback
      const audioUrl = getRecordingUrl(uploadResult.file_url);

      // Create recording take with R2 URL
      const take = {
        id: uploadResult.recording_id,
        trackId: selectedTrackId || 'default',
        audioUrl, // Now using R2 URL instead of object URL
        startTime: 0, // TODO: Calculate based on timeline position
        duration: recordingTime,
        waveform,
        selected: false,
        timestamp: Date.now(),
      };

      // Add to store
      addRecordingTake(take);

      // Reset state
      audioChunksRef.current = [];
      setRecordingTime(0);
      setIsUploading(false);
    } catch (err) {
      console.error('Failed to save recording:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save recording. Please try again.';
      setError(errorMessage);
      setIsUploading(false);
    }
  };

  // Generate waveform data from audio blob
  const generateWaveform = async (blob: Blob): Promise<number[]> => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const channelData = audioBuffer.getChannelData(0);
      const samples = 100; // Number of waveform points
      const blockSize = Math.floor(channelData.length / samples);
      const waveform: number[] = [];

      for (let i = 0; i < samples; i++) {
        const start = i * blockSize;
        const end = start + blockSize;
        let sum = 0;
        
        for (let j = start; j < end; j++) {
          sum += Math.abs(channelData[j]);
        }
        
        waveform.push(sum / blockSize);
      }

      audioContext.close();
      return waveform;
    } catch (err) {
      console.error('Failed to generate waveform:', err);
      return Array(100).fill(0);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Delete recording take
  const deleteTake = (takeId: string) => {
    removeRecordingTake(takeId);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Audio Recorder</h3>
        <p className="text-sm text-zinc-400">
          Record audio from your microphone and save multiple takes
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">Error</p>
            <p className="text-sm text-red-300 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Track Selection */}
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2">
          Record to Track
        </label>
        <select
          value={selectedTrackId || ''}
          onChange={(e) => setSelectedTrackId(e.target.value || null)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isRecording}
        >
          <option value="">Select a track...</option>
          {channels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {channel.name}
            </option>
          ))}
        </select>
      </div>

      {/* Recording Controls */}
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Start/Stop Button */}
            {!isRecording ? (
              <button
                onClick={startRecording}
                disabled={!selectedTrackId}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
              >
                <Mic className="w-5 h-5" />
                Start Recording
              </button>
            ) : (
              <>
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 px-6 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-medium transition-colors"
                >
                  <Square className="w-5 h-5" />
                  Stop
                </button>
                <button
                  onClick={togglePause}
                  className="flex items-center gap-2 px-6 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg font-medium transition-colors"
                >
                  {isPaused ? (
                    <>
                      <Play className="w-5 h-5" />
                      Resume
                    </>
                  ) : (
                    <>
                      <Pause className="w-5 h-5" />
                      Pause
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Recording Timer */}
          <div className="text-2xl font-mono text-white">
            {formatTime(recordingTime)}
          </div>
        </div>

        {/* Input Level Meter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">Input Level</span>
            <span className="text-sm text-zinc-400">
              {Math.round(inputLevel * 100)}%
            </span>
          </div>
          <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-100"
              style={{ width: `${inputLevel * 100}%` }}
            />
          </div>
        </div>

        {/* Recording Status */}
        {isRecording && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-zinc-300">
              {isPaused ? 'Recording Paused' : 'Recording...'}
            </span>
          </div>
        )}

        {isUploading && (
          <div className="mt-4 flex items-center gap-2 text-sm text-blue-400">
            <Upload className="w-4 h-4 animate-pulse" />
            <span>Uploading to cloud storage...</span>
          </div>
        )}
      </div>

      {/* Recorded Takes */}
      <div>
        <h4 className="text-lg font-medium text-white mb-3">
          Recorded Takes ({recordingTakes.length})
        </h4>
        {recordingTakes.length === 0 ? (
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-6 text-center">
            <p className="text-zinc-400">No recordings yet</p>
            <p className="text-sm text-zinc-500 mt-1">
              Start recording to create your first take
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {recordingTakes.map((take, index) => (
              <div
                key={take.id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">
                      Take {recordingTakes.length - index}
                    </span>
                    <span className="text-sm text-zinc-400">
                      {formatTime(take.duration)}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {new Date(take.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  
                  {/* Waveform Preview */}
                  <div className="mt-2 h-12 flex items-center gap-0.5">
                    {take.waveform.map((value, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-blue-500/50 rounded-sm"
                        style={{ height: `${value * 100}%` }}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <audio src={take.audioUrl} controls className="h-10" />
                  <button
                    onClick={() => deleteTake(take.id)}
                    className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete take"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
